import type { GridApi, GridOptions } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";

import { useCallback, useEffect, useRef } from "react";

import { diffGridOptions, WRAPPER_DERIVED_GRID_OPTION_KEYS } from "~/components/grid/reactiveGridOptions";
import logger from "~/helpers/logger";

type ManagedGridOptions<TData> = Parameters<GridApi<TData>["updateGridOptions"]>[0];

interface UseReactiveGridOptionsParams<TData> {
	gridDataKey: string;
	gridOptions?: GridOptions<TData>;
	gridRef: React.RefObject<AgGridReact<TData>>;
	wrapperResolvedOptions?: Partial<GridOptions<TData>>;
}

const warnCreationTimeOnlyChanges = (creationTimeOnlyKeys: string[], gridDataKey: string): void => {
	if (!import.meta.env.DEV || creationTimeOnlyKeys.length === 0) return;
	logger.warn(
		`[AgGrid:${gridDataKey}] gridOptions "${creationTimeOnlyKeys.join('", "')}" changed after the grid was created, but AG Grid reads these only at creation time — the change was NOT applied. Set them before first render, or remount the grid with a new React key.`
	);
};

const warnNotAutoForwardableChanges = (notAutoForwardableKeys: string[], gridDataKey: string): void => {
	if (!import.meta.env.DEV || notAutoForwardableKeys.length === 0) return;
	logger.warn(
		`[AgGrid:${gridDataKey}] gridOptions.${notAutoForwardableKeys.join(", ")} changed after grid creation. The wrapper does NOT auto-apply it, because doing so would push the raw value straight past the wrapper's applyUserPreferences step and wipe persisted column renames, order and widths. Call useGridLayout().updateColumnDefs(api, colDefs) from the consuming component instead. Note: AgGridWithoutRibbon has no such escape hatch — a grid on that wrapper must be remounted with a new React key to change its columns.`
	);
};

const withDerivedKeys = (keys: string[], wrapperResolvedOptions: object): string[] => [
	...new Set(
		keys.flatMap((key) => [
			key,
			...(WRAPPER_DERIVED_GRID_OPTION_KEYS[key] ?? []).filter((derived) => derived in wrapperResolvedOptions)
		])
	)
];

const withWrapperResolvedValues = <TData>(
	updates: Partial<GridOptions<TData>>,
	wrapperResolvedOptions: Partial<GridOptions<TData>>
): Partial<GridOptions<TData>> =>
	Object.fromEntries(
		withDerivedKeys(Object.keys(updates), wrapperResolvedOptions).map((key) => [
			key,
			key in wrapperResolvedOptions
				? wrapperResolvedOptions[key as keyof GridOptions<TData>]
				: updates[key as keyof GridOptions<TData>]
		])
	);

const applyGridOptionUpdates = <TData>(api: GridApi<TData>, updates: Partial<GridOptions<TData>>): void => {
	if (Object.keys(updates).length === 0) return;
	api.updateGridOptions(updates as ManagedGridOptions<TData>);
	// AG Grid registers no property listener for `context`, so live cells keep stale params until refreshed.
	if ("context" in updates) api.refreshCells({ force: true });
};

const useReactiveGridOptions = <TData>({
	gridDataKey,
	gridOptions,
	gridRef,
	wrapperResolvedOptions
}: UseReactiveGridOptionsParams<TData>): ((api: GridApi<TData>) => void) => {
	const appliedGridOptions = useRef(gridOptions);
	const latestGridOptions = useRef(gridOptions);
	const latestWrapperResolvedOptions = useRef(wrapperResolvedOptions);
	const hasWarnedNotAutoForwardable = useRef(false);

	const applyLatestGridOptions = useCallback(
		(api: GridApi<TData>) => {
			if (appliedGridOptions.current === latestGridOptions.current || api.isDestroyed()) return;

			const { creationTimeOnlyKeys, notAutoForwardableKeys, updates } = diffGridOptions(
				appliedGridOptions.current,
				latestGridOptions.current
			);
			appliedGridOptions.current = latestGridOptions.current;

			warnCreationTimeOnlyChanges(creationTimeOnlyKeys, gridDataKey);
			if (notAutoForwardableKeys.length > 0 && !hasWarnedNotAutoForwardable.current) {
				hasWarnedNotAutoForwardable.current = true;
				warnNotAutoForwardableChanges(notAutoForwardableKeys, gridDataKey);
			}
			applyGridOptionUpdates(api, withWrapperResolvedValues(updates, latestWrapperResolvedOptions.current ?? {}));
		},
		[gridDataKey]
	);

	useEffect(() => {
		latestGridOptions.current = gridOptions;
		latestWrapperResolvedOptions.current = wrapperResolvedOptions;
		const api = gridRef.current?.api;
		if (!api) return;
		applyLatestGridOptions(api);
	});

	return applyLatestGridOptions;
};

export default useReactiveGridOptions;
